// src/modules/calendar/components/GoogleCalendarConnect.jsx
import { useState } from 'react';
import { useGoogleCalendar } from '../../../contexts/GoogleCalendarContext';
import { analyzeCalendarPatterns } from '../../../services/gemini';
import { FiRefreshCw, FiLink2, FiAlertCircle, FiZap, FiChevronDown, FiChevronUp, FiTrendingUp, FiClock, FiBookOpen, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';

const PRIORIDAD_COLOR = {
  alta: 'bg-red-50 border-red-200 text-red-800',
  media: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  baja: 'bg-green-50 border-green-200 text-green-800',
};

const IMPACTO_COLOR = {
  alto: 'text-red-600',
  medio: 'text-yellow-600',
  bajo: 'text-green-600',
};

const GoogleCalendarConnect = ({ compact = false }) => {
  const {
    isConnected,
    isConnecting,
    lastSyncTime,
    syncError,
    accessToken,
    connectCalendar,
    disconnectCalendar,
    syncEvents,
  } = useGoogleCalendar();

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setAnalysis(null);
    try {
      // Obtener 4 meses de eventos (2 pasados + 2 futuros)
      const now = new Date();
      const timeMin = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      const timeMax = new Date(now.getFullYear(), now.getMonth() + 2, 0);

      toast.loading('Leyendo tu calendario...', { id: 'gcal-analysis' });
      const events = await syncEvents(timeMin, timeMax);

      if (!events || events.length === 0) {
        toast.error('No se encontraron eventos en el rango seleccionado', { id: 'gcal-analysis' });
        return;
      }

      toast.loading(`Analizando ${events.length} eventos con IA...`, { id: 'gcal-analysis' });
      const result = await analyzeCalendarPatterns(events);
      setAnalysis(result);
      setShowAnalysis(true);
      toast.success('Análisis completado', { id: 'gcal-analysis' });
    } catch (error) {
      console.error('Error en análisis:', error);
      toast.error('Error al analizar: ' + error.message, { id: 'gcal-analysis' });
    } finally {
      setIsAnalyzing(false);
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {isConnected ? (
          <>
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-xs text-green-700 font-medium">Google Calendar</span>
          </>
        ) : syncError ? (
          <button
            onClick={connectCalendar}
            disabled={isConnecting}
            className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 transition"
          >
            <FiAlertCircle size={14} />
            Reconectar
          </button>
        ) : (
          <button
            onClick={connectCalendar}
            disabled={isConnecting}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition font-medium"
          >
            <FiLink2 size={14} />
            {isConnecting ? 'Conectando...' : 'Conectar Google Calendar'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header del componente */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <svg viewBox="0 0 24 24" width="22" height="22" className="text-blue-600">
                <path fill="currentColor" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"/>
              </svg>
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">Google Calendar</p>
              <p className="text-xs text-gray-500">
                {isConnected ? 'Conectado (solo lectura)' : 'No conectado'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isConnected ? (
              <>
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                {/* Botón Analizar con IA */}
                <button
                  onClick={handleAnalyze}
                  disabled={isAnalyzing}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-xs rounded-lg hover:bg-purple-700 transition disabled:opacity-50 font-medium"
                >
                  {isAnalyzing ? (
                    <>
                      <FiRefreshCw className="animate-spin" size={13} />
                      Analizando...
                    </>
                  ) : (
                    <>
                      <FiZap size={13} />
                      Analizar con IA
                    </>
                  )}
                </button>
                <button
                  onClick={disconnectCalendar}
                  className="text-xs text-red-500 hover:text-red-700 transition font-medium"
                >
                  Desconectar
                </button>
              </>
            ) : (
              <button
                onClick={connectCalendar}
                disabled={isConnecting}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-1 font-medium"
              >
                {isConnecting ? (
                  <>
                    <FiRefreshCw className="animate-spin" size={14} />
                    Conectando...
                  </>
                ) : (
                  <>
                    <FiLink2 size={14} />
                    Conectar
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {syncError && (
          <div className="mt-2 p-2 bg-orange-50 border border-orange-100 rounded-lg">
            <p className="text-xs text-orange-700 flex items-center gap-1">
              <FiAlertCircle size={12} />
              {syncError}
            </p>
          </div>
        )}

        {isConnected && lastSyncTime && (
          <p className="text-xs text-gray-400 mt-2">
            Última sincronización: {lastSyncTime.toLocaleTimeString('es-ES')}
          </p>
        )}
      </div>

      {/* Panel de análisis */}
      {analysis && (
        <div className="border-t border-gray-100">
          {/* Toggle mostrar/ocultar */}
          <button
            onClick={() => setShowAnalysis(!showAnalysis)}
            className="w-full flex items-center justify-between px-4 py-3 bg-purple-50 hover:bg-purple-100 transition text-left"
          >
            <div className="flex items-center gap-2">
              <FiZap className="text-purple-600" size={15} />
              <span className="text-sm font-semibold text-purple-800">Análisis IA de tu Calendario</span>
              <span className="text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">
                {analysis.estadisticas?.totalEventos || 0} eventos analizados
              </span>
            </div>
            {showAnalysis ? <FiChevronUp className="text-purple-600" /> : <FiChevronDown className="text-purple-600" />}
          </button>

          {showAnalysis && (
            <div className="p-4 space-y-5">
              {/* Resumen */}
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed">
                {analysis.resumen}
              </div>

              {/* Estadísticas rápidas */}
              {analysis.estadisticas && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-xl font-bold text-blue-700">{analysis.estadisticas.cursosDetectados}</p>
                    <p className="text-xs text-blue-600">Cursos detectados</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-xl font-bold text-purple-700">{analysis.estadisticas.totalEventos}</p>
                    <p className="text-xs text-purple-600">Eventos totales</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-sm font-bold text-green-700">{(analysis.estadisticas.diasConMasCarga || []).join(', ') || '-'}</p>
                    <p className="text-xs text-green-600">Días con más carga</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-sm font-bold text-orange-700">{(analysis.estadisticas.horariosPopulares || []).join(', ') || '-'}</p>
                    <p className="text-xs text-orange-600">Horarios populares</p>
                  </div>
                </div>
              )}

              {/* Cursos detectados */}
              {analysis.cursos && analysis.cursos.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                    <FiBookOpen size={14} /> Cursos detectados
                  </h4>
                  <div className="space-y-2">
                    {analysis.cursos.map((curso, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                        <span className="font-medium text-gray-800">{curso.nombre}</span>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{curso.sesionesDetectadas} sesiones</span>
                          <span className="flex items-center gap-1"><FiClock size={11} /> {curso.frecuencia}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Patrones */}
              {analysis.patrones && analysis.patrones.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                    <FiTrendingUp size={14} /> Patrones encontrados
                  </h4>
                  <div className="space-y-2">
                    {analysis.patrones.map((patron, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg text-sm">
                        <span className={`text-xs font-bold uppercase mt-0.5 ${IMPACTO_COLOR[patron.impacto] || 'text-gray-500'}`}>
                          {patron.impacto}
                        </span>
                        <p className="text-gray-700">{patron.descripcion}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recomendaciones */}
              {analysis.recomendaciones && analysis.recomendaciones.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 mb-2 flex items-center gap-1">
                    <FiCheckCircle size={14} /> Recomendaciones IA
                  </h4>
                  <div className="space-y-2">
                    {analysis.recomendaciones.map((rec, i) => (
                      <div key={i} className={`p-3 rounded-lg border text-sm ${PRIORIDAD_COLOR[rec.prioridad] || 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                        <div className="flex items-start gap-2">
                          <FiAlertTriangle size={14} className="mt-0.5 shrink-0" />
                          <div>
                            <p className="font-semibold">{rec.titulo}</p>
                            <p className="text-xs mt-0.5 opacity-80">{rec.detalle}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarConnect;
